import { useEffect } from "react";
import { PdfPreview } from "./PdfPreview";

interface Props {
  url: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Full-screen overlay that renders the lease at the widest reasonable size
 * so phone users can read every clause without pinch-zooming. Locks body
 * scroll while open and closes on Escape or backdrop tap.
 */
export function PdfFullscreenModal({ url, open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Full lease preview"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white border-b border-slate-700">
        <div className="text-sm font-medium">Lease preview</div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm font-medium"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Close
        </button>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-6">
        <div className="mx-auto max-w-3xl">
          <PdfPreview url={url} maxWidth={1100} />
        </div>
      </div>
    </div>
  );
}
